import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { buildListDocumentsQuery } from "../../utils/fileManagerQuery";
import "./Search.css";

const EMPTY_FILTERS = {
  search: "",
  range: "30d",
  date_field: "received_at",
  date_from: "",
  date_to: "",
  issue_date_from: "",
  issue_date_to: "",
  end_at_from: "",
  end_at_to: "",
  amount_min: "",
  amount_max: "",
  doc_type: "",
  invoice_subtype: "",
  supplier_name: "",
  supplier_cuit: "",
  status: "",
  currency: "",
  confidence_band: "",
  channel: "",
  orderBy: "updated_at",
  orderDirection: "desc",
};

const PERIODS = [
  ["1h", "Última hora"],
  ["24h", "Hoy"],
  ["7d", "7 días"],
  ["30d", "30 días"],
  ["all", "Todo"],
  ["custom", "Personalizado"],
];

const DOC_TYPES = [
  ["", "Todos"],
  ["invoices", "Facturas"],
  ["receipts", "Recibos"],
  ["unclassified", "Sin clasificar"],
];

const INVOICE_SUBTYPES = [
  ["", "Todos"],
  ["INVOICE", "Factura"],
  ["CREDITO", "Nota de crédito"],
  ["DEBITO", "Nota de débito"],
];

const STATUS_OPTIONS = [
  ["", "Todos"],
  ["PROCESS_COMPLETED", "Procesado"],
  ["PROCESS_FAILED", "Error"],
  ["OCR_IN_PROGRESS", "En proceso"],
];

const CONFIDENCE = [
  ["", "Todas"],
  ["high", "Alta"],
  ["medium", "Media"],
  ["low", "Baja"],
];

function rangeLabel(value) {
  return PERIODS.find(([v]) => v === value)?.[1] || value;
}

function isoDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function filtersWithRange(filters) {
  const next = { ...filters };
  delete next.range;
  delete next.date_field;
  delete next.date_from;
  delete next.date_to;
  next.issue_date_from = filters.issue_date_from || "";
  next.issue_date_to = filters.issue_date_to || "";
  next.end_at_from = filters.end_at_from || "";
  next.end_at_to = filters.end_at_to || "";
  if (filters.range === "all") return next;
  if (filters.range === "custom") {
    const from = filters.date_from || filters.issue_date_from || filters.end_at_from || "";
    const to = filters.date_to || filters.issue_date_to || filters.end_at_to || "";
    if (filters.date_field === "issue_date") {
      next.issue_date_from = from ? from.slice(0, 10) : "";
      next.issue_date_to = to ? to.slice(0, 10) : "";
      next.end_at_from = "";
      next.end_at_to = "";
    } else {
      next.end_at_from = from ? new Date(`${from.slice(0, 10)}T00:00:00`).toISOString() : "";
      next.end_at_to = to ? new Date(`${to.slice(0, 10)}T23:59:59`).toISOString() : "";
      next.issue_date_from = "";
      next.issue_date_to = "";
    }
    return next;
  }
  const since = filters.range === "1h"
    ? new Date(Date.now() - 60 * 60 * 1000).toISOString()
    : isoDaysAgo(filters.range === "24h" ? 1 : filters.range === "7d" ? 7 : 30);
  if (filters.date_field === "issue_date") {
    next.issue_date_from = since.slice(0, 10);
    next.end_at_from = "";
  } else {
    next.end_at_from = since;
    next.issue_date_from = "";
  }
  return next;
}

function cleanFilters(filters) {
  return Object.fromEntries(
    Object.entries(filtersWithRange(filters)).filter(([, value]) => value !== "" && value != null),
  );
}

function activeFilterChips(filters) {
  const labels = [];
  const add = (key, label) => {
    if (filters[key]) labels.push({ key, label });
  };
  add("search", `Texto: ${filters.search}`);
  if (filters.range && filters.range !== "all") {
    const customRange = filters.range === "custom" && (filters.date_from || filters.date_to)
      ? `Personalizado: ${filters.date_from || "inicio"} → ${filters.date_to || "hoy"}`
      : rangeLabel(filters.range);
    add("range", customRange);
  }
  add("doc_type", DOC_TYPES.find(([v]) => v === filters.doc_type)?.[1]);
  add("invoice_subtype", INVOICE_SUBTYPES.find(([v]) => v === filters.invoice_subtype)?.[1]);
  add("supplier_name", `Proveedor: ${filters.supplier_name}`);
  add("supplier_cuit", `CUIT: ${filters.supplier_cuit}`);
  add("amount_min", `Desde $${filters.amount_min}`);
  add("amount_max", `Hasta $${filters.amount_max}`);
  add("currency", filters.currency);
  add("confidence_band", CONFIDENCE.find(([v]) => v === filters.confidence_band)?.[1]);
  add("status", STATUS_OPTIONS.find(([v]) => v === filters.status)?.[1]);
  add("channel", `Origen: ${filters.channel}`);
  return labels.filter((chip) => chip.label);
}

const Search = ({
  onSearch,
  loading,
  placeholder = "Buscar en Tagnize",
  initialFilters = null,
  initialFiltersKey = null,
  appliedFilters = null,
  appliedFiltersKey = null,
}) => {
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS, ...(initialFilters || {}) });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const chips = useMemo(() => activeFilterChips(filters), [filters]);

  const runSearch = useCallback((nextFilters = filters) => {
    const query = buildListDocumentsQuery(cleanFilters(nextFilters));
    if (onSearch) {
      onSearch({ query });
    }
  }, [filters, onSearch]);

  const handleSearchClick = () => {
    runSearch(filters);
  };

  const update = (patch, { run = false } = {}) => {
    const next = { ...filters, ...patch };
    setFilters(next);
    if (run) runSearch(next);
  };

  const updateFilter = (patch) => update(patch, { run: true });

  const clearAll = () => {
    setFilters(EMPTY_FILTERS);
    runSearch(EMPTY_FILTERS);
  };

  const removeChip = (key) => {
    if (key === "range") {
      update({
        range: "all",
        date_from: "",
        date_to: "",
        issue_date_from: "",
        issue_date_to: "",
        end_at_from: "",
        end_at_to: "",
      }, { run: true });
      return;
    }
    update({ [key]: EMPTY_FILTERS[key] }, { run: true });
  };

  useEffect(() => {
    if (!initialFilters || !initialFiltersKey) return;
    const next = { ...EMPTY_FILTERS, ...initialFilters };
    setFilters(next);
    onSearch?.({ query: buildListDocumentsQuery(cleanFilters(next)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiltersKey]);

  useEffect(() => {
    if (!appliedFilters || appliedFiltersKey == null) return;
    const next = { ...EMPTY_FILTERS, ...appliedFilters };
    setFilters(next);
    onSearch?.({ query: buildListDocumentsQuery(cleanFilters(next)) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFiltersKey]);

  const drawer = drawerOpen ? createPortal(
    <div className="filter-drawer-backdrop" onClick={() => setDrawerOpen(false)}>
      <aside className="filter-drawer" onClick={(e) => e.stopPropagation()}>
        <header>
          <div>
            <p>Filtros avanzados</p>
            <strong>Buscador inteligente</strong>
          </div>
          <button type="button" onClick={() => setDrawerOpen(false)}>×</button>
        </header>

        <section className="filter-group">
          <h4>Período — campo de fecha</h4>
          <div className="field-toggle">
            <button
              type="button"
              className={filters.date_field === "issue_date" ? "active" : ""}
              onClick={() => updateFilter({ date_field: "issue_date" })}
            >
              Emisión
            </button>
            <button
              type="button"
              className={filters.date_field === "received_at" ? "active" : ""}
              onClick={() => updateFilter({ date_field: "received_at" })}
            >
              Recepción
            </button>
          </div>
          <div className="drawer-period-grid">
            {PERIODS.map(([value, label]) => (
              <button
                key={value}
                type="button"
                className={`drawer-period-chip ${filters.range === value ? "active" : ""}`}
                onClick={() => updateFilter({ range: value })}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="date-row">
            <label>
              Desde personalizado
              <input
                className="date-input"
                type="date"
                value={filters.date_from}
                onFocus={() => update({ range: "custom" })}
                onChange={(e) => updateFilter({ date_from: e.target.value, range: "custom" })}
              />
            </label>
            <label>
              Hasta personalizado
              <input
                className="date-input"
                type="date"
                value={filters.date_to}
                onFocus={() => update({ range: "custom" })}
                onChange={(e) => updateFilter({ date_to: e.target.value, range: "custom" })}
              />
            </label>
          </div>
          <p className="drawer-hint">
            “Personalizado” queda activo automáticamente al tocar cualquiera de estas fechas.
          </p>
        </section>

        <section className="filter-group">
          <h4>Rango de importes</h4>
          <div className="range-inputs">
            <label>
              Mínimo
              <input type="number" value={filters.amount_min} onChange={(e) => updateFilter({ amount_min: e.target.value })} placeholder="mín" />
            </label>
            <label>
              Máximo
              <input type="number" value={filters.amount_max} onChange={(e) => updateFilter({ amount_max: e.target.value })} placeholder="máx" />
            </label>
          </div>
        </section>

        <section className="filter-group">
          <h4>Tipo de comprobante</h4>
          <label>
            Tipo
            <select value={filters.doc_type} onChange={(e) => updateFilter({ doc_type: e.target.value, invoice_subtype: e.target.value === "invoices" ? filters.invoice_subtype : "" })}>
              {DOC_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          {filters.doc_type === "invoices" && (
            <label>
              Subtipo fiscal
              <select value={filters.invoice_subtype} onChange={(e) => updateFilter({ invoice_subtype: e.target.value })}>
                {INVOICE_SUBTYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </label>
          )}
        </section>

        <section className="filter-group">
          <h4>Proveedor</h4>
          <label>
            Nombre
            <input value={filters.supplier_name} onChange={(e) => updateFilter({ supplier_name: e.target.value })} placeholder="Nombre o razón social" />
          </label>
          <label>
            CUIT proveedor
            <input value={filters.supplier_cuit} onChange={(e) => updateFilter({ supplier_cuit: e.target.value })} placeholder="30-..." />
          </label>
        </section>

        <section className="filter-group">
          <h4>Estado, moneda y origen</h4>
          <label>
            Estado
            <select value={filters.status} onChange={(e) => updateFilter({ status: e.target.value })}>
              {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
          <label>
            Moneda
            <select value={filters.currency} onChange={(e) => updateFilter({ currency: e.target.value })}>
              <option value="">Todas</option>
              <option value="ARS">ARS</option>
              <option value="USD">USD</option>
            </select>
          </label>
          <label>
            Origen
            <select value={filters.channel} onChange={(e) => updateFilter({ channel: e.target.value })}>
              <option value="">Todos</option>
              <option value="webapp">App</option>
              <option value="email">Email</option>
              <option value="api">API</option>
            </select>
          </label>
        </section>

        <section className="filter-group">
          <h4>Nivel de confianza</h4>
          <div className="seg-conf">
            {CONFIDENCE.filter(([value]) => value).map(([value, label]) => (
              <button
                key={value}
                type="button"
                data-b={value}
                className={filters.confidence_band === value ? "active" : ""}
                onClick={() => updateFilter({ confidence_band: filters.confidence_band === value ? "" : value })}
              >
                {label}
              </button>
            ))}
          </div>
        </section>

        <section className="filter-group">
          <h4>Ordenamiento</h4>
          <label>
            Orden
            <select value={`${filters.orderBy}:${filters.orderDirection}`} onChange={(e) => {
              const [orderBy, orderDirection] = e.target.value.split(":");
              updateFilter({ orderBy, orderDirection });
            }}>
              <option value="updated_at:desc">Recepción reciente</option>
              <option value="issue_date:desc">Emisión reciente</option>
              <option value="issue_date:asc">Emisión antigua</option>
              <option value="total_amount:desc">Importe mayor</option>
              <option value="total_amount:asc">Importe menor</option>
              <option value="supplier_name:asc">Proveedor A-Z</option>
            </select>
          </label>
        </section>

        <footer>
          <button type="button" className="clear-filters" onClick={clearAll}>Limpiar</button>
          <button type="button" className="apply-filters" onClick={() => { setDrawerOpen(false); runSearch(filters); }}>Aplicar filtros</button>
        </footer>
      </aside>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      <div className="search-container">
        <div className="search-form smart-search-form">
          <div className="search-input-wrapper">
            <input
              type="text"
              className="search-input"
              placeholder={placeholder}
              value={filters.search}
              onChange={(e) => update({ search: e.target.value })}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearchClick();
              }}
            />
            <button
              className="search-button"
              disabled={loading}
              onClick={handleSearchClick}
            >
              {loading ? "Buscando..." : "Buscar"}
            </button>
          </div>
        </div>

        <div className="period-row">
          {PERIODS.map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={`period-chip ${filters.range === value ? "active" : ""}`}
              onClick={() => {
                update({ range: value }, { run: value !== "custom" });
                if (value === "custom") setDrawerOpen(true);
              }}
            >
              {label}
            </button>
          ))}
          <button type="button" className="advanced-filter-button" onClick={() => setDrawerOpen(true)}>
            Filtros {chips.length ? `(${chips.length})` : ""}
          </button>
        </div>

        {chips.length > 0 && (
          <div className="active-filter-row">
            {chips.map((chip) => (
              <button key={chip.key} type="button" className="active-filter-chip" onClick={() => removeChip(chip.key)}>
                {chip.label} <span>×</span>
              </button>
            ))}
            <button type="button" className="clear-filters" onClick={clearAll}>Limpiar todo</button>
          </div>
        )}
      </div>
      {drawer}
    </>
  );
};

export default Search;
